import {useMemo} from 'react';

import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {useLocation} from 'react-router-dom';

import {ENDPOINTS} from 'constants/endpoints';
import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {apiConfig} from 'libs/api/api';
import {flags} from 'libs/rollout';
import {getUrlParameter} from 'libs/url/url';

import {RETURNING_CUSTOMER_URL_QUERY} from '../constants';
import {getIsReturningCustomerCL} from '../utils';

type UseCheckOrderType = UseQueryResult & {
    isExp: boolean;
    isReturningCustomer: boolean;
    isCheckOrderDone: boolean;
};

/**
 * Makes a request to the backend to check if the current user made a CL purchase.
 * In the response the backend sets the cookie "returningCustomer" if the user made a purchase.
 */
export const useCheckOrder = ({enabled} = {enabled: true}): UseCheckOrderType => {
    const {search} = useLocation();
    const isReturningCustomerURL = getUrlParameter(RETURNING_CUSTOMER_URL_QUERY, search) === 'true';
    const isExp = flags.dReturningCustomerCL.isEnabled();
    const isReturningCustomerCookie = getIsReturningCustomerCL();
    const {is_logged_in: isLoggedIn} = useGetCustomerData();

    const result = useQuery({
        enabled: isExp && !isReturningCustomerCookie && enabled,
        queryFn: () =>
            apiConfig.instanceGusaBackend({
                method: 'get',
                params: {
                    ...(isReturningCustomerURL ? {returningCustomer: true} : {}),
                },
                url: ENDPOINTS.CHECK_ORDER,
            }),
        queryKey: ['CHECK_ORDER', `${isReturningCustomerURL}`, `${isLoggedIn}`],
        staleTime: Infinity,
    });

    const {isError, isFetched, isSuccess} = result;

    const isRequestDone = isFetched && (isSuccess || isError);

    const isReturningCustomerRequestIsDone = useMemo(() => {
        if (!isExp) return true;
        if (isReturningCustomerCookie || isRequestDone) return true;
        return false;
    }, [isRequestDone, isExp, isReturningCustomerCookie]);

    return {
        isCheckOrderDone: isReturningCustomerRequestIsDone,
        isExp,
        isReturningCustomer: isReturningCustomerCookie,
        ...result,
    };
};
